/**
 * useMyNumbers — shared outbound-number selection for the user.
 *
 * One unified selection across voice and SMS, shared by every picker on
 * the page (header PhoneDialer, MessageThread composer, QueueActionBar)
 * via a module-level store. Cross-tab sync via the `storage` event.
 *
 * Smart-default policy (per docs/outbound-number-selection.md):
 *   1. The user picks the number — `setFromNumber()` always wins for the
 *      current contact view.
 *   2. The "from" is always visible — capability badges + the picker show
 *      what is selected.
 *   3. Defaults are smart per contact — when the caller passes a contact
 *      summary, the hook resolves a suggested-from from the cached
 *      picker-context and applies it whenever the contact identity
 *      changes. The user can still override via `setFromNumber`; that
 *      override holds until the next contact change.
 *
 * Performance: the picker-context (accessible numbers + tenant-wide
 * brand/agency association maps) is fetched ONCE per session and cached
 * at the module level. Per-contact-switch cost is zero HTTP, zero SQL —
 * the suggested-from is computed locally from the cached context plus
 * the contact's already-loaded `agency_id` / `agency_brand_id` /
 * `agency_brand` fields. The cache invalidates on a `phone-config-changed`
 * WS event (admin tags or untags a phone) and on a soft TTL.
 */

import { useEffect, useMemo, useState } from 'react';
import { apiGet } from './useApi';

export interface MyNumber {
  id: string;
  phone_number: string;
  friendly_name: string;
  voice_enabled: boolean;
  sms_enabled: boolean;
  mms_enabled: boolean;
}

/**
 * Minimal contact shape the picker reads to compute the suggested-from.
 * Caller passes whatever it has loaded; missing fields just skip the
 * matching ladder rung. Brand identity prefers the joined agency record
 * (#978) and falls back to the contact's own `brand_id` / `brand` fields
 * (#1064) — tens of thousands of contacts carry a brand directly but have
 * no (or an unbranded) agency, and the bulk "Change brand" action writes
 * the contact-level field. Mirrors ContactPanel's display fallback
 * (`agencies.brand or contacts.brand`), so the suggested number always
 * tracks the brand the rep actually sees.
 */
export interface PickerContact {
  agency_id?: string | null;
  agency_brand_id?: string | null;
  agency_brand?: string | null;
  /** Contact's own brand FK — fallback when the agency has no brand. */
  brand_id?: string | null;
  /** Contact's own brand label — fallback for the name-matching rungs. */
  brand?: string | null;
}

interface PickerContextResponse {
  phone_numbers: MyNumber[];
  brand_associations: Record<string, string[]>;
  agency_associations: Record<string, string[]>;
  /** brand id → display name, for resolving a brand *label* to its
   *  association row when the contact only carries brand text (#1064). */
  brand_names: Record<string, string>;
}

interface MyNumbersState {
  numbers: MyNumber[];
  loading: boolean;
  /** voice-enabled subset (capability-gated action buttons use this count) */
  voiceNumbers: MyNumber[];
  /** sms-enabled subset */
  smsNumbers: MyNumber[];
  /** unified from-number (E.164), '' if nothing accessible */
  fromNumber: string;
  /** true when current selection supports voice calls */
  canVoice: boolean;
  /** true when current selection supports SMS */
  canSms: boolean;
  setFromNumber: (next: string) => void;
}

// ── Module-level shared store ────────────────────────────────────────────

type Setter = (next: string) => void;

let shared: string = '';
const subscribers = new Set<Setter>();

function publish(next: string): void {
  if (next === shared) return;
  shared = next;
  subscribers.forEach((fn) => fn(next));
}

// ── Picker-context cache ────────────────────────────────────────────────
//
// One fetch per session. Subsequent useMyNumbers mounts reuse the cached
// context. Invalidation: dropContextCache() clears the singleton; the
// next mount refetches. The WebSocketProvider already subscribes to the
// `phone-config` channel; surfaces that consume `useWebSocket` should
// listen for `phone-config-changed` and call `dropContextCache()`.

const CONTEXT_TTL_MS = 5 * 60 * 1000;

// Picker-context source. The main app reads the rep-centric
// /api/voice/picker-context; the AO portal shell points this at
// /portal/voice/picker-context (#1426) — same response shape, portal
// number set (agency-associated ∩ accessible, plus direct-assigned).
// A surface switch drops the cache so numbers never leak across shells.
let pickerContextUrl = '/api/voice/picker-context';

export function configurePickerContextUrl(url: string): void {
  if (url === pickerContextUrl) return;
  pickerContextUrl = url;
  contextCache = null;
  notifyContext(null);
}

interface PickerContextCache {
  promise: Promise<PickerContextResponse>;
  fetchedAt: number;
}

let contextCache: PickerContextCache | null = null;
const contextSubscribers = new Set<(ctx: PickerContextResponse | null) => void>();
let lastContext: PickerContextResponse | null = null;

function notifyContext(ctx: PickerContextResponse | null): void {
  lastContext = ctx;
  contextSubscribers.forEach((fn) => fn(ctx));
}

async function loadContext(): Promise<PickerContextResponse> {
  const now = Date.now();
  if (contextCache && now - contextCache.fetchedAt < CONTEXT_TTL_MS) {
    return contextCache.promise;
  }
  const promise = apiGet<PickerContextResponse>(pickerContextUrl)
    .then((data) => {
      const normalized: PickerContextResponse = {
        phone_numbers: data.phone_numbers ?? [],
        brand_associations: data.brand_associations ?? {},
        agency_associations: data.agency_associations ?? {},
        brand_names: data.brand_names ?? {},
      };
      notifyContext(normalized);
      return normalized;
    })
    .catch((err) => {
      // Reset on failure so the next caller retries instead of latching
      // onto a rejected promise forever.
      contextCache = null;
      throw err;
    });
  contextCache = { promise, fetchedAt: now };
  return promise;
}

/**
 * Drop the cached picker-context. Call from a `phone-config-changed` WS
 * handler when an admin tags or untags a phone, so the next picker fetch
 * sees the new association map.
 */
export function dropContextCache(): void {
  contextCache = null;
  notifyContext(null);
}

// ── Resolver ────────────────────────────────────────────────────────────

/**
 * Normalize a brand/phone label for fuzzy comparison: lowercase, collapse
 * internal whitespace runs to a single space, and trim. Mirrors the
 * server-side normalizeLabel in src/routes/voice-data.ts (#708).
 */
function normalizeLabel(label: string): string {
  return label.toLowerCase().replace(/\s+/g, ' ').trim();
}

/**
 * Pure function. Picks the suggested-from `phone_number` (E.164 string)
 * for a contact given the cached picker-context. Returns `''` when
 * nothing matches; the caller falls back to the first accessible number.
 *
 * Match precedence (first hit wins):
 *   1. Phone tagged to the contact's `agency_brand_id` (joined agency's
 *      brand). Reflects "this whole brand's outbound traffic uses these
 *      numbers."
 *   2. Phone tagged to the contact's own `brand_id` (#1064) — fallback
 *      brand identity for contacts whose agency is missing or unbranded,
 *      and for brands set via the bulk "Change brand" action.
 *   3. Phone tagged to the contact's `agency_id`. Reflects "this agency
 *      has its own dedicated number."
 *   4. Brand *label* resolved to a brands row by name (via the
 *      `brand_names` map), then through its association (#1064) — covers
 *      contacts that carry only brand text (e.g. "National Income Life")
 *      with no brand_id and no branded agency.
 *   5. Phone whose `friendly_name` matches the brand text (agency's
 *      first, then the contact's own), case- and whitespace-insensitive
 *      — covers the common setup where the tenant simply names a phone
 *      "American Income" instead of creating an explicit association row.
 */
export function pickSuggestedFromNumber(
  context: PickerContextResponse,
  contact: PickerContact | null | undefined,
): string {
  if (!contact) return '';
  const accessibleById = new Map(context.phone_numbers.map((n) => [n.id, n]));

  const fromBrandAssociation = (brandId: string | null | undefined): string => {
    if (!brandId) return '';
    const ids = context.brand_associations[brandId];
    if (!ids) return '';
    const hit = ids.find((id) => accessibleById.has(id));
    return hit ? accessibleById.get(hit)!.phone_number : '';
  };

  // 1–2. Explicit brand association — joined agency's brand first (#978),
  // then the contact's own brand (#1064).
  const agencyBrandHit = fromBrandAssociation(contact.agency_brand_id);
  if (agencyBrandHit) return agencyBrandHit;
  const ownBrandHit = fromBrandAssociation(contact.brand_id);
  if (ownBrandHit) return ownBrandHit;

  // 3. Explicit agency association.
  if (contact.agency_id) {
    const ids = context.agency_associations[contact.agency_id];
    if (ids) {
      const hit = ids.find((id) => accessibleById.has(id));
      if (hit) return accessibleById.get(hit)!.phone_number;
    }
  }

  // Brand labels in display precedence: agency brand, then contact brand.
  const labels = [contact.agency_brand, contact.brand]
    .map((l) => (l ? normalizeLabel(l) : ''))
    .filter((l) => l !== '');

  // 4. Brand label → brands row by name → association.
  for (const label of labels) {
    const brandId = Object.keys(context.brand_names).find(
      (id) => normalizeLabel(context.brand_names[id] ?? '') === label,
    );
    const hit = fromBrandAssociation(brandId);
    if (hit) return hit;
  }

  // 5. Brand label → phone friendly_name.
  for (const label of labels) {
    const nameHit = context.phone_numbers.find((n) => normalizeLabel(n.friendly_name) === label);
    if (nameHit) return nameHit.phone_number;
  }

  return '';
}

// ── Hook ────────────────────────────────────────────────────────────────

export function useMyNumbers(
  contact?: PickerContact | null,
  opts?: {
    /**
     * Restrict the selectable numbers to those associated with the contact's
     * own agency (#1508). Used by the AO portal so the "from" number is
     * automatically and only the viewed agency's line — never another agency
     * the owner also controls. When the agency has no dedicated number, the
     * agency's brand line(s) are next; only then the full accessible set, so
     * the owner is never stranded. Mirrors the server's send ladder. Defaults
     * off; every rep-facing surface keeps the full list unchanged.
     */
    restrictToContactAgency?: boolean;
  },
): MyNumbersState {
  const restrictToContactAgency = opts?.restrictToContactAgency ?? false;
  const [context, setContext] = useState<PickerContextResponse | null>(lastContext);
  const [loading, setLoading] = useState(lastContext === null);
  const [fromNumber, setLocal] = useState<string>(shared);

  useEffect(() => {
    subscribers.add(setLocal);
    if (shared !== fromNumber) setLocal(shared);
    return () => {
      subscribers.delete(setLocal);
    };
  }, [fromNumber]);

  // Subscribe to context changes so a `dropContextCache()` from a WS
  // event triggers a fresh load on every consumer of the hook.
  useEffect(() => {
    contextSubscribers.add(setContext);
    return () => {
      contextSubscribers.delete(setContext);
    };
  }, []);

  // Load (or refresh) the picker-context. Re-runs when context is
  // invalidated to null by `dropContextCache()`.
  useEffect(() => {
    if (context !== null) {
      if (loading) setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    loadContext()
      .then((ctx) => {
        if (cancelled) return;
        setContext(ctx);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[useMyNumbers] picker-context fetch failed:', err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [context, loading]);

  // Per-contact smart default. Whenever the contact identity changes
  // (new contact, fields populated by a profile load, association cache
  // refreshed by a WS event), apply the suggested-from. Clears any
  // user override from the previous contact view — that's the bug fix
  // for "picker stuck on first contact's choice."
  const hasContact = contact != null;
  const agency_id = contact?.agency_id ?? null;
  const agency_brand_id = contact?.agency_brand_id ?? null;
  const agency_brand = contact?.agency_brand ?? null;
  const brand_id = contact?.brand_id ?? null;
  const brand = contact?.brand ?? null;

  // Effective selectable list. Normally the full accessible set; in
  // restricted mode (#1508) it is narrowed to the viewed contact's agency
  // numbers, then the agency's brand numbers, falling back to the full set
  // when neither has any so the caller is never left with an empty picker.
  const effectiveNumbers = useMemo(() => {
    const all = context?.phone_numbers ?? [];
    if (!restrictToContactAgency || !context) return all;
    const narrow = (ids: string[] | undefined): MyNumber[] => {
      if (!ids || ids.length === 0) return [];
      const idSet = new Set(ids);
      return all.filter((n) => idSet.has(n.id));
    };
    const byAgency = agency_id ? narrow(context.agency_associations[agency_id]) : [];
    if (byAgency.length > 0) return byAgency;
    const brandKey = agency_brand_id ?? brand_id;
    const byBrand = brandKey ? narrow(context.brand_associations[brandKey]) : [];
    if (byBrand.length > 0) return byBrand;
    return all;
  }, [context, restrictToContactAgency, agency_id, agency_brand_id, brand_id]);

  useEffect(() => {
    if (!context) return;
    const list = effectiveNumbers;
    if (list.length === 0) {
      if (shared !== '') publish('');
      return;
    }
    if (hasContact) {
      const suggested = pickSuggestedFromNumber(context, {
        agency_id,
        agency_brand_id,
        agency_brand,
        brand_id,
        brand,
      });
      // A non-empty smart-default for the active contact always wins —
      // that is the per-contact override behavior the picker needed (the
      // bug this PR fixes was that a stale shared value silently
      // shadowed every later contact's suggestion). Only honor a suggestion
      // that is actually selectable in the current list — in restricted
      // mode a cross-agency suggestion must not slip through.
      if (suggested && list.some((n) => n.phone_number === suggested)) {
        if (suggested !== shared) publish(suggested);
        return;
      }
      // Restricted mode (#1508): the picker is locked to the viewed agency's
      // (or its brand's) number(s). With no matching suggestion, default to
      // the first of those rather than leaving a stale cross-agency pick.
      if (restrictToContactAgency) {
        const first = list[0]?.phone_number ?? '';
        if (first !== shared) publish(first);
        return;
      }
      // Contact has no agency / brand to derive a default from. Don't
      // stomp on a still-valid user pick from a previous contact view —
      // fall back to first-accessible only if the current shared value
      // is invalid.
    }
    // No contact context (e.g. PhoneDialer) or contact with no agency
    // hint: keep an existing valid pick; otherwise fall back to first
    // accessible.
    const pickValid = shared !== '' && list.some((n) => n.phone_number === shared);
    if (pickValid) return;
    const fallback = list[0]?.phone_number ?? '';
    if (fallback !== shared) publish(fallback);
  }, [
    context,
    effectiveNumbers,
    restrictToContactAgency,
    hasContact,
    agency_id,
    agency_brand_id,
    agency_brand,
    brand_id,
    brand,
  ]);

  const numbers = effectiveNumbers;
  const voiceNumbers = useMemo(() => numbers.filter((n) => n.voice_enabled), [numbers]);
  const smsNumbers = useMemo(() => numbers.filter((n) => n.sms_enabled), [numbers]);
  const current = useMemo(
    () => numbers.find((n) => n.phone_number === fromNumber),
    [numbers, fromNumber],
  );
  const canVoice = !!current?.voice_enabled;
  const canSms = !!current?.sms_enabled;

  return {
    numbers,
    loading,
    voiceNumbers,
    smsNumbers,
    fromNumber,
    canVoice,
    canSms,
    setFromNumber: publish,
  };
}
